/**
 * OneBase Clientes
 * Página de gestión de clientes del ERP OneBase
 */

import React, { useState, useEffect } from 'react';
import { clienteService } from '../services/onebaseService';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import '../styles/onebase-clientes.css';

import Loading from '../components/Loading';
const OneBaseClientes = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    rut: '',
    email: '',
    telefono: '',
    contacto_principal: '',
    direccion: '',
    ciudad: '',
  });

  useEffect(() => {
    cargarClientes();
  }, []);

  const cargarClientes = async () => {
    setLoading(true);
    try {
      const response = await clienteService.getAll({ status: 'activo' });
      const clientesData = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setClientes(clientesData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCliente) {
        await clienteService.update(selectedCliente.id, formData);
        toast.success('Cliente actualizado');
      } else {
        await clienteService.create(formData);
        toast.success('Cliente creado');
      }
      setShowForm(false);
      resetForm();
      cargarClientes();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.non_field_errors?.[0] || 'Error al guardar cliente');
    }
  };

  const handleEdit = (cliente) => {
    setSelectedCliente(cliente);
    setFormData({
      nombre: cliente.nombre,
      rut: cliente.rut || '',
      email: cliente.email || '',
      telefono: cliente.telefono || '',
      contacto_principal: cliente.contacto_principal || '',
      direccion: cliente.direccion || '',
      ciudad: cliente.ciudad || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar este cliente?')) {
      try {
        await clienteService.delete(id);
        toast.success('Cliente eliminado');
        cargarClientes();
      } catch (error) {
        toast.error('Error al eliminar');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      rut: '',
      email: '',
      telefono: '',
      contacto_principal: '',
      direccion: '',
      ciudad: '',
    });
    setSelectedCliente(null);
  };

  const filteredClientes = clientes.filter(
    (c) =>
      c.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.rut?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="onebase-clientes">
      <div className="page-header">
        <h1>Clientes</h1>
        <button
          className="estado-btn"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <FiPlus /> Nuevo Cliente
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Buscar por nombre o RUT..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading ? (
        <Loading fullScreen={false} />
      ) : filteredClientes.length > 0 ? (
        <div className="clientes-grid">
          {filteredClientes.map((cliente) => (
            <div key={cliente.id} className="cliente-card">
              <div className="card-header">
                <h3>{cliente.nombre}</h3>
                <div className="card-actions">
                  <button
                    className="btn-icon"
                    onClick={() => handleEdit(cliente)}
                    title="Editar"
                  >
                    <FiEdit />
                  </button>
                  <button
                    className="btn-icon danger"
                    onClick={() => handleDelete(cliente.id)}
                    title="Eliminar"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>

              <div className="card-body">
                {cliente.rut && (
                  <p>
                    <strong>RUT:</strong> {cliente.rut}
                  </p>
                )}
                {cliente.contacto_principal && (
                  <p>
                    <strong>Contacto:</strong> {cliente.contacto_principal}
                  </p>
                )}
                {cliente.email && (
                  <p>
                    <strong>Email:</strong> <a href={`mailto:${cliente.email}`}>{cliente.email}</a>
                  </p>
                )}
                {cliente.telefono && (
                  <p>
                    <strong>Teléfono:</strong> {cliente.telefono}
                  </p>
                )}
                {cliente.ciudad && (
                  <p>
                    <strong>Ciudad:</strong> {cliente.ciudad}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <p>No hay clientes</p>
          <button
            className="btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            Crear primer cliente
          </button>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedCliente ? 'Editar Cliente' : 'Nuevo Cliente'}</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nombre *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>RUT</label>
                  <input
                    type="text"
                    name="rut"
                    value={formData.rut}
                    onChange={handleInputChange}
                    placeholder="12.345.678-9"
                  />
                </div>
                <div className="form-group">
                  <label>Contacto Principal</label>
                  <input
                    type="text"
                    name="contacto_principal"
                    value={formData.contacto_principal}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Ciudad</label>
                <input
                  type="text"
                  name="ciudad"
                  value={formData.ciudad}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Dirección</label>
                <textarea
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleInputChange}
                  rows="2"
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {selectedCliente ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OneBaseClientes;
